/**
 * This component's code has been copied from the @react-three-fiber/drei repo.
 * In particular, content from these files has been copied:
 * - https://github.com/pmndrs/drei/blob/master/src/core/ScreenSizer.tsx
 * - https://github.com/pmndrs/drei/blob/master/src/core/calculateScaleFactor.ts
 * - https://github.com/pmndrs/drei/blob/master/src/helpers/ts-utils.tsx#L12
 *
 * ScreenSizer was introduced in a newer version of
 * @react-three-fiber/drei than this repository uses. Upgrading
 * @react-three-fiber/drei causes build errors, which would be harder to fix
 * than copying this bit of code.
 */

import { Object3DProps, useFrame } from '@react-three/fiber';
import * as React from 'react';
import {
  forwardRef,
  useRef,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type RefAttributes,
} from 'react';
import { Object3D, Vector3 } from 'three';
import { calculateScaleFactor } from './utils/calculate-scale-factor';

/**
 * Utility type to declare the type of a `forwardRef` component so that the type is not "evaluated" in the declaration
 * file.
 */
export type ForwardRefComponent<P, T> = ForwardRefExoticComponent<
  PropsWithoutRef<P> & RefAttributes<T>
>;

const worldPos = /* @__PURE__ */ new Vector3();

export interface ScreenSizerProps extends Object3DProps {
  /** Scale factor. Defaults to 1, which equals 1 pixel size. */
  scale?: number;
}

/**
 * Wraps children in an `Object3D` and attempts to scale from
 * world units to screen units * scale factor.
 *
 * For example, this will render a box of roughly 1x1 pixel size,
 * independently of how far the camera is.
 *
 * ```jsx
 * <ScreenSizer>
 *   <Box />
 * </ScreenSizer>
 * ```
 */
export const ScreenSizer: ForwardRefComponent<ScreenSizerProps, Object3D> =
  /* @__PURE__ */ forwardRef<Object3D, ScreenSizerProps>(
    ({ scale = 1, ...props }, ref) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const container = useRef<Object3D>(null!);
      React.useImperativeHandle(ref, () => container.current, []);

      useFrame((state) => {
        const obj = container.current;
        if (!obj) return;
        const sf = calculateScaleFactor(
          obj.getWorldPosition(worldPos),
          scale,
          state.camera,
          state.size
        );
        obj.scale.setScalar(sf * scale);
      });

      return <object3D ref={container} {...props} />;
    }
  );

ScreenSizer.displayName = 'ScreenSizer';
